const { produce, createStore } = window.theme.cart.functions;

/**
 * Shopify API returns an array of items with duplicate keys in case the same
 * item is both discounted, and an initiator of the discount. This function
 * removes the duplicate keys, adding a string of discount value to them.
 *
 * @param {Array} cart Cart object returned from Shopify API
 * @returns Cart object with items with amended keys
 */
const deDupeItemsInCart = (cart) => ({
  ...cart,
  items: cart.items.map((item) => {
    let newItem;
    if (
      item.line_level_discount_allocations &&
      item.line_level_discount_allocations.length > 0
    ) {
      newItem = {
        ...item,
        key: `${item.key}${item.line_level_discount_allocations.reduce(
          (discountsString, discount) => {
            return `${discountsString}_discount-${discount.amount}`;
          },
          '',
        )}`,
      };
    } else {
      newItem = item;
    }

    return newItem;
  }),
});

/**
 * Package deals are sold at a bundle price that only holds per bike, so what a
 * cart holds of a package follows the bikes bought with it, one for one: three
 * bikes carry three packages, and the last bike out takes the package with it.
 * Nothing caps the total — a customer may buy as many packages as they have
 * bikes to put them against, in as many colours as they like.
 *
 * Three line item properties the package product page writes identify a pair:
 * `_requires_bike` on the package, `Type: Main Bike` on the bike, and `Package`
 * on the bike naming the package it was bought with. That last one is what ties
 * a package to its bikes, so a bike leaving the cart moves only the package it
 * names and leaves every other package alone. A bike that reached the cart some
 * other way carries none of them, so its product type counts as well — it just
 * anchors no package, having been bought with none.
 */
const PACKAGE_PROPERTY = '_requires_bike';
const BIKE_PROPERTY = 'Type';
const BIKE_PROPERTY_VALUE = 'Main Bike';
const BIKE_PRODUCT_TYPE = 'ebike';
const PACKAGE_LINK_PROPERTY = 'Package';

const itemIsBike = (item) =>
  item.properties?.[BIKE_PROPERTY] === BIKE_PROPERTY_VALUE ||
  (item.product_type || '').trim().toLowerCase() === BIKE_PRODUCT_TYPE;

const itemRequiresBike = (item) =>
  item.properties?.[PACKAGE_PROPERTY] === 'true';

/**
 * Titles are compared rather than ids because the title is all the bike line
 * carries, and it is written from the package product's own title. Case and
 * surrounding space are ignored so a tidied up product title cannot quietly
 * orphan the packages already sitting in people's carts.
 */
const normalisedTitle = (title) => (title || '').trim().toLowerCase();

/**
 * How many of the bikes a package was bought with are still in the cart, which
 * is how many of that package the cart should be holding. Zero means the
 * package is on its own, whatever else the cart holds.
 */
const bikesForPackage = (items, packageItem) => {
  const title = normalisedTitle(packageItem.product_title);
  if (!title) return 0;

  return items.reduce((total, item) => {
    if (!itemIsBike(item)) return total;
    if (normalisedTitle(item.properties?.[PACKAGE_LINK_PROPERTY]) !== title)
      return total;

    return total + item.quantity;
  }, 0);
};

/**
 * The packages a cart cannot keep: the ones with no bike of theirs left in it.
 */
const orphanedPackages = (cart) => {
  const items = cart?.items || [];

  return items.filter(
    (item) => itemRequiresBike(item) && bikesForPackage(items, item) === 0,
  );
};

const cartNeedsBike = (cart) => orphanedPackages(cart).length > 0;

/**
 * Raises already asked of Shopify, so a package it will not give any more of is
 * asked for once rather than again on every cart update that follows. Keyed by
 * line and by the quantity wanted, so another bike added asks the question
 * afresh. Store keys carry the discount suffix deDupeItemsInCart adds and raw
 * cart.js keys do not, so the suffix comes off before a key is remembered.
 */
const attemptedRaises = new Set();

const raiseKey = (key, quantity) =>
  `${String(key).split('_discount-')[0]}:${quantity}`;

const rememberRaises = (updates, raises) =>
  raises.forEach((key) => attemptedRaises.add(raiseKey(key, updates[key])));

const withoutRaises = (updates, raises) =>
  Object.fromEntries(
    Object.entries(updates).filter(([key]) => !raises.includes(key)),
  );

/**
 * The package lines in a cart, gathered by the package they are of. A package
 * bought twice over can hold more than one line — a second variant of it, or a
 * different set of properties — and the bike lines name a package by title
 * alone, so the count they answer is the count of the whole group. Weighed line
 * by line instead, each line would read the group's bikes as its own and follow
 * them up, leaving the cart with a multiple of what it should hold.
 */
const packageGroups = (items) => {
  const groups = new Map();

  items.filter(itemRequiresBike).forEach((item) => {
    const title = normalisedTitle(item.product_title);
    const group = groups.get(title);

    if (group) group.push(item);
    else groups.set(title, [item]);
  });

  return Array.from(groups.values());
};

/**
 * What has to change for the packages in a cart to be allowed, as quantities to
 * send to cart/update.js, or null while there is nothing to put right.
 *
 * One rule, applied to each package on its own: the cart holds as many of a
 * package as it holds of the bikes bought with it. A package whose bikes have
 * all gone comes out, a package that outnumbers its bikes comes down to them,
 * and a package the customer has bought another bike for follows it up.
 * Packages do not count against each other at all — a cart with three bikes can
 * hold three packages, in whatever colours those bikes came in.
 *
 * Coming down protects the price of the deal and always happens. Going up is a
 * convenience, and stock may stand in the way, so it is asked for once and then
 * left alone however Shopify answers — raises are reported separately for that
 * reason, and because a raise is the customer's own doing and needs no notice.
 *
 * The reason travels with the updates because it is what the cart tells the
 * customer afterwards, and the cases read nothing like each other. A cart that
 * manages more than one at once leads with the removal, the larger change, and
 * a cart that only follows its bikes up says nothing at all.
 */
const packageCorrections = (cart) => {
  const items = cart?.items || [];
  const groups = packageGroups(items);

  if (groups.length === 0) return null;

  const updates = {};
  const raises = [];
  let orphaned = false;
  let reduced = false;

  groups.forEach((lines) => {
    const bikes = bikesForPackage(items, lines[0]);
    const held = lines.reduce((total, item) => total + item.quantity, 0);

    if (held === bikes) return;

    if (bikes === 0) {
      lines.forEach((item) => {
        updates[item.key] = 0;
      });
      orphaned = true;
      return;
    }

    if (held > bikes) {
      // Off the most recently added line first, so what the customer is left
      // holding is what they bought first.
      let surplus = held - bikes;

      [...lines].reverse().forEach((item) => {
        if (surplus === 0) return;

        const taken = Math.min(surplus, item.quantity);
        updates[item.key] = item.quantity - taken;
        surplus -= taken;
      });
      reduced = true;
      return;
    }

    // Short of its bikes: one line carries the whole shortfall, since the bikes
    // name the package and not the line.
    const line = lines[0];
    const wanted = line.quantity + (bikes - held);

    if (!attemptedRaises.has(raiseKey(line.key, wanted))) {
      updates[line.key] = wanted;
      raises.push(line.key);
    }
  });

  if (Object.keys(updates).length === 0) return null;

  let reason = null;
  if (orphaned) reason = 'no-bike';
  else if (reduced) reason = 'limit';

  return { updates, raises, reason };
};

const PACKAGE_PRODUCT_TYPE = 'package';

/**
 * Whether a product from the recommendations endpoint is a package.
 *
 * The line item properties above are no help here: nothing has been added to a
 * cart yet, and the product JSON the endpoint returns carries neither the
 * template suffix nor the collections that product-is-package.liquid reads. So
 * the ids come the other way, rendered into the page by layout/theme.liquid,
 * and are read on each call rather than at module load so the order the two
 * scripts run in cannot matter.
 *
 * Product type is read as well, the way itemIsBike reads it for a bike that
 * reached the cart without the package properties on it. It covers a package
 * that the collection scan missed — one kept out of the packages collection, or
 * sitting past the 50 products a Liquid loop will walk.
 */
const productIsPackage = (product) => {
  const ids = window.theme.cart?.packageProductIds || [];

  if (ids.some((id) => String(id) === String(product?.id))) return true;

  const type = product?.type ?? product?.product_type ?? '';

  return type.trim().toLowerCase() === PACKAGE_PRODUCT_TYPE;
};

window.theme.cart.packages = {
  itemIsBike,
  itemRequiresBike,
  bikesForPackage,
  orphanedPackages,
  cartNeedsBike,
  packageCorrections,
  productIsPackage,
};

const fetchCart = async () => {
  try {
    const data = await fetch(`${window.Shopify.routes.root}cart.js`);
    const cart = await data.json();

    return deDupeItemsInCart(cart);
  } catch (e) {
    throw new Error(`Could not update cart: "${e}"`);
  }
};

/**
 * Sets the given line items to the given quantities in one request, a quantity
 * of zero being a removal. The keys have to come from a raw cart.js response
 * rather than the store, whose keys carry the discount suffix deDupeItemsInCart
 * adds to work around Shopify returning the same key twice.
 */
const updateLineItems = async (updates) => {
  const data = await fetch(`${window.Shopify.routes.root}cart/update.js`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ updates }),
  });

  if (!data.ok) throw new Error('Could not update the packages in the cart');

  return data.json();
};

/**
 * Sends a set of package corrections, and should Shopify turn the request down,
 * sends it again without the raises — a package cannot always follow its bike
 * up, and the stock that stopped it must not take a reduction or an orphaned
 * package's removal down with it.
 */
const applyPackageCorrections = async ({ updates, raises }) => {
  rememberRaises(updates, raises);

  try {
    return await updateLineItems(updates);
  } catch (e) {
    const required = withoutRaises(updates, raises);

    if (Object.keys(required).length === 0) return null;
    if (raises.length === 0) throw e;

    return updateLineItems(required);
  }
};

const clearCart = async () => {
  try {
    const data = await fetch(`${window.Shopify.routes.root}cart/clear.js`, {
      method: 'POST',
    });
    const cart = await data.json();

    return cart;
  } catch (e) {
    throw new Error(`Could not clear cart: "${e}"`);
  }
};

const resetFreeShippingAnimation = () => {
  if (window.theme.cart.store.getState().products.total_price === 0)
    localStorage.removeItem('freeShippingAnimationShown');
};

const initiateStore = () => {
  const initialCartDataJson = document.getElementById('cart-data');
  const initialCartData = initialCartDataJson
    ? JSON.parse(initialCartDataJson.textContent)
    : {};

  const giftWrappingDataJson = document.getElementById(
    'cart-gift-wrapping-data',
  );
  const giftWrappingData = {
    giftWrapping: {
      ...(giftWrappingDataJson
        ? {
            ...JSON.parse(giftWrappingDataJson.textContent),
          }
        : {
            productId: null,
            wrapIndividually: false,
          }),
      statusBeingUpdated: false,
      messageBeingUpdated: false,
    },
  };

  window.theme.cart.store = createStore((set, get) => ({
    ...giftWrappingData,
    lineItemsBeingUpdated: [],
    latestAddedProduct: null,
    noteBeingUpdated: false,
    packageRemoval: null,
    ongoingUpdates: {
      items: {},
      note: null,
      giftWrappingMessage: null,
    },
    products: deDupeItemsInCart(initialCartData),
    recommendations: {
      enabled: false,
      intent: 'related',
      items: {},
    },
    productVariantsBeingAdded: [],
    clear: async () => {
      const products = await clearCart();

      set(
        produce((draft) => {
          draft.lineItemsBeingUpdated = [];
          draft.latestAddedProduct = null;
          draft.products = products;
          draft.recommendations = {};
          draft.productVariantsBeingAdded = [];
        }),
      );
    },
    /**
     * Brings every package in the cart back into step with the bikes it was
     * bought with: out when they have all gone, down when it outnumbers them,
     * up when the customer has bought another. The cart is read back from
     * Shopify rather than trusted from the store, so a cart changed in another
     * tab is caught too.
     *
     * Resolves to true when the cart changed, so a caller can tell the customer
     * why it changed under them.
     */
    validateCartPackages: async () => {
      const data = await fetch(`${window.Shopify.routes.root}cart.js`);
      const cart = await data.json();
      const corrections = packageCorrections(cart);

      if (!corrections) return false;

      const products = await applyPackageCorrections(corrections);

      // Every correction was a raise, and stock would not have it. The cart is
      // as it was, so there is nothing to say and nothing to write.
      if (!products) return false;

      set(
        produce((draft) => {
          draft.products = deDupeItemsInCart(products);
          // Stamped rather than flagged: the cart tells the customer what
          // happened, and a second correction needs to read as a new one even
          // while the notice from the first is still up. A package following
          // its bikes up is the customer's own doing, and is left unstamped.
          if (corrections.reason) {
            draft.packageRemoval = {
              id: Date.now(),
              reason: corrections.reason,
            };
          }
        }),
      );

      await get().updateGiftWrapping();
      await get().updateRecommendations();

      return true;
    },
    add: async (variants) => {
      if (!variants) throw new Error('Variant(s) must be specified');

      const items = (Array.isArray(variants) ? variants : [variants]).map(
        (item) => {
          if (Object.keys(item).find((key) => key.includes('properties'))) {
            return Object.entries(item).reduce((newItem, [key, value]) => {
              if (key.includes('properties') && value !== '') {
                if (!newItem.properties) newItem.properties = {};
                newItem.properties[
                  key.replace('properties[', '').replace(']', '')
                ] = value;
              } else {
                newItem[key] = value;
              }

              return newItem;
            }, {});
          }
          return item;
        },
      );

      set(
        produce((draft) => {
          draft.productVariantsBeingAdded = items.map((item) => item.id);
        }),
      );

      const addedResponseData = await fetch(
        `${window.Shopify.routes.root}cart/add.js`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ items }),
        },
      );
      const addedResponse = await addedResponseData.json();
      const products = await fetchCart();

      const prevItemsCount = get().products.item_count;

      set(
        produce((draft) => {
          draft.latestAddedProduct = addedResponse.items
            ? addedResponse.items[0]
            : null;
          draft.products = products;
        }),
      );

      if (prevItemsCount !== products.item_count) {
        await get().updateGiftWrapping();
      }
      await get().updateRecommendations();

      if (addedResponse.status) throw new Error(addedResponse.description);
    },
    change: async (key, quantity = 0) => {
      if (!key) throw new Error('Line item key must be specified');

      if (get().ongoingUpdates.items[key]) {
        get().ongoingUpdates.items[key].abort();
        set(
          produce((draft) => {
            draft.ongoingUpdates.items[key] = null;
          }),
        );
      }

      const controller = new AbortController();
      const signal = controller.signal;

      set(
        produce((draft) => {
          if (!get().lineItemsBeingUpdated.includes(key)) {
            draft.lineItemsBeingUpdated.push(key);
          }
          draft.ongoingUpdates.items[key] = controller;
        }),
      );

      // Need to use line and not the key due to Shopify
      // returning duplicate keys for same discounted items
      // TODO: Monitor API status, if the bug gets solved, replace with keys,
      // and remove dedupe function
      const line =
        get().products.items.findIndex((item) => item.key === key) + 1;
      const changedResponseData = await fetch(
        `${window.Shopify.routes.root}cart/change.js`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            // id: key,
            line,
            quantity,
          }),
          signal,
        },
      );
      const changedResponse = await changedResponseData.json();
      let products;

      if (changedResponse.status === 422) {
        products = await fetchCart();
      } else {
        products = changedResponse;
      }

      const prevItemsCount = get().products.item_count;

      // Sometimes change.js returns random line items with quantity of 0
      // in the items array (in particular when discounts are applied),
      // so we need to filter them out
      products = {
        ...products,
        items: products.items.filter((item) => item.quantity > 0),
      };

      products = deDupeItemsInCart(products);

      set(
        produce((draft) => {
          draft.lineItemsBeingUpdated = draft.lineItemsBeingUpdated.filter(
            (k) => k !== key,
          );
          draft.products = products;
          draft.ongoingUpdates.items[key] = null;
        }),
      );

      if (prevItemsCount !== products.item_count) {
        await get().updateGiftWrapping();
      }
      await get().updateRecommendations();

      if (changedResponse.status === 422)
        throw new Error(changedResponse.description);
    },
    enableRecommendations: async (intent = 'related') => {
      if (get().recommendations.enabled) return;

      set(
        produce((draft) => {
          draft.recommendations.enabled = true;
          draft.recommendations.intent = intent;
        }),
      );

      await get().updateRecommendations();
    },
    resetLatestAddedProduct: () => {
      set(
        produce((draft) => {
          draft.latestAddedProduct = null;
        }),
      );
    },
    resetVariantsBeingAdded: (variantId = '') => {
      set(
        produce((draft) => {
          draft.productVariantsBeingAdded = variantId
            ? draft.productVariantsBeingAdded.filter((id) => id !== variantId)
            : [];
        }),
      );
    },
    setGiftWrapping: async (enable = true) => {
      const giftWrappingProductId = get().giftWrapping.productId;
      if (!giftWrappingProductId) return;

      const cart = get().products;
      const giftWrappingIndividual = get().giftWrapping.wrapIndividually;
      if (enable) {
        set(
          produce((draft) => {
            draft.giftWrapping.statusBeingUpdated = true;
          }),
        );

        const savedGiftWrappingProductId =
          get().products.attributes['_gift-wrapping-product-id'];
        const giftWrappingResponseData = await fetch(
          `${window.Shopify.routes.root}cart/update.js`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              attributes: {
                ...(savedGiftWrappingProductId !== giftWrappingProductId
                  ? {
                      '_gift-wrapping-product-id': giftWrappingProductId,
                    }
                  : {}),
                'gift-wrapping': enable,
                'gift-wrapping-message': '',
              },
              updates: {
                [giftWrappingProductId]: giftWrappingIndividual
                  ? cart.items.reduce(
                      (nonGiftWrappingQuantity, item) =>
                        item.variant_id !== giftWrappingProductId
                          ? nonGiftWrappingQuantity + item.quantity
                          : nonGiftWrappingQuantity,
                      0,
                    )
                  : 1,
              },
            }),
          },
        );
        const products = await giftWrappingResponseData.json();

        set(
          produce((draft) => {
            draft.giftWrapping.statusBeingUpdated = false;
            draft.products = deDupeItemsInCart(products);
          }),
        );
      } else {
        let signal = null;
        let key = null;
        const giftWrappingItem = get().products.items.find(
          (item) => item.variant_id === giftWrappingProductId,
        );

        set(
          produce((draft) => {
            draft.giftWrapping.statusBeingUpdated = true;
          }),
        );

        if (giftWrappingItem) {
          key = giftWrappingItem.key;

          if (get().ongoingUpdates.items[key]) {
            get().ongoingUpdates.items[key].abort();
            set(
              produce((draft) => {
                draft.ongoingUpdates.items[key] = null;
              }),
            );
          }

          const controller = new AbortController();
          signal = controller.signal;

          set(
            produce((draft) => {
              if (!get().lineItemsBeingUpdated.includes(key)) {
                draft.lineItemsBeingUpdated.push(key);
              }
              draft.ongoingUpdates.items[key] = controller;
            }),
          );
        }

        const giftWrappingResponseData = await fetch(
          `${window.Shopify.routes.root}cart/update.js`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              attributes: {
                '_gift-wrapping-product-id': null,
                'gift-wrapping': enable,
                'gift-wrapping-message': '',
              },
              updates: {
                [giftWrappingProductId]: 0,
              },
            }),
            signal,
          },
        );
        const products = await giftWrappingResponseData.json();

        set(
          produce((draft) => {
            if (key) {
              draft.lineItemsBeingUpdated = draft.lineItemsBeingUpdated.filter(
                (k) => k !== key,
              );
            }
            draft.giftWrapping.statusBeingUpdated = false;
            draft.products = deDupeItemsInCart(products);
          }),
        );
      }
    },
    syncGiftWrapping: async () => {
      const giftWrappingProductId = get().giftWrapping.productId;
      const savedGiftWrappingProductId =
        get().products.attributes['_gift-wrapping-product-id'];

      if (
        savedGiftWrappingProductId &&
        savedGiftWrappingProductId !== giftWrappingProductId
      ) {
        const savedGiftWrappingProduct = get().products.items.find(
          (item) => item.id === savedGiftWrappingProductId,
        );

        if (savedGiftWrappingProduct) {
          if (get().ongoingUpdates.items[savedGiftWrappingProduct.key]) {
            get().ongoingUpdates.items[savedGiftWrappingProduct.key].abort();
            set(
              produce((draft) => {
                draft.ongoingUpdates.items[savedGiftWrappingProduct.key] = null;
              }),
            );
          }

          const controller = new AbortController();
          const signal = controller.signal;

          set(
            produce((draft) => {
              if (
                !get().lineItemsBeingUpdated.includes(
                  savedGiftWrappingProduct.key,
                )
              ) {
                draft.lineItemsBeingUpdated.push(savedGiftWrappingProduct.key);
              }
              draft.ongoingUpdates.items[savedGiftWrappingProduct.key] =
                controller;
            }),
          );

          const giftWrappingResponseData = await fetch(
            `${window.Shopify.routes.root}cart/update.js`,
            {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                attributes: {
                  '_gift-wrapping-product-id': null,
                },
                updates: {
                  [savedGiftWrappingProductId]: 0,
                },
              }),
              signal,
            },
          );
          const products = await giftWrappingResponseData.json();

          set(
            produce((draft) => {
              draft.lineItemsBeingUpdated = draft.lineItemsBeingUpdated.filter(
                (k) => k !== savedGiftWrappingProduct.key,
              );
              draft.giftWrapping.statusBeingUpdated = false;
              draft.products = deDupeItemsInCart(products);
            }),
          );
        }
      }

      if (!giftWrappingProductId) return;

      const giftWrappingEnabled = Boolean(
        get().products.attributes['gift-wrapping'],
      );
      const giftWrappingIndividual = get().giftWrapping.wrapIndividually;
      const giftWrappingItem = get().products.items.find(
        (item) => item.variant_id === giftWrappingProductId,
      );

      if (!giftWrappingEnabled && giftWrappingItem) {
        await get().setGiftWrapping(false);
      }

      if (giftWrappingEnabled && !giftWrappingItem) {
        await get().setGiftWrapping(true);
      }

      if (
        giftWrappingEnabled &&
        giftWrappingItem &&
        giftWrappingIndividual &&
        giftWrappingItem.quantity === 1
      ) {
        await get().updateGiftWrapping();
      }

      if (
        giftWrappingEnabled &&
        giftWrappingItem &&
        !giftWrappingIndividual &&
        giftWrappingItem.quantity > 1
      ) {
        await get().updateGiftWrapping();
      }
    },
    updateGiftWrapping: async () => {
      const giftWrappingProductId = get().giftWrapping.productId;
      if (!giftWrappingProductId) return;

      const giftWrappingIndividual = get().giftWrapping.wrapIndividually;
      const giftWrappingItem = get().products.items.find(
        (item) => item.variant_id === giftWrappingProductId,
      );

      if (
        !giftWrappingItem ||
        (get().products.items_count > 1 &&
          !giftWrappingIndividual &&
          giftWrappingItem)
      )
        return;

      const key = giftWrappingItem.key;

      if (get().ongoingUpdates.items[key]) {
        get().ongoingUpdates.items[key].abort();
        set(
          produce((draft) => {
            draft.ongoingUpdates.items[key] = null;
          }),
        );
      }

      const controller = new AbortController();
      const signal = controller.signal;

      set(
        produce((draft) => {
          if (!get().lineItemsBeingUpdated.includes(key)) {
            draft.lineItemsBeingUpdated.push(key);
          }
          draft.ongoingUpdates.items[key] = controller;
        }),
      );

      let giftWrappingQuantity = giftWrappingIndividual
        ? get().products.items.reduce(
            (nonGiftWrappingQuantity, item) =>
              item.variant_id !== giftWrappingProductId
                ? nonGiftWrappingQuantity + item.quantity
                : nonGiftWrappingQuantity,
            0,
          )
        : 1;

      if (
        giftWrappingItem &&
        get().products.items.filter((i) => i.id !== giftWrappingProductId)
          .length === 0
      ) {
        giftWrappingQuantity = 0;
      }

      const giftWrappingAttributeUpdate =
        giftWrappingQuantity === 0
          ? {
              attributes: {
                'gift-wrapping': false,
                'gift-wrapping-message': '',
              },
            }
          : {};

      const data = await fetch(`${window.Shopify.routes.root}cart/update.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...giftWrappingAttributeUpdate,
          updates: {
            [giftWrappingProductId]: giftWrappingQuantity,
          },
        }),
        signal,
      });
      const cart = await data.json();

      set(
        produce((draft) => {
          draft.lineItemsBeingUpdated = draft.lineItemsBeingUpdated.filter(
            (k) => k !== key,
          );
          draft.products = deDupeItemsInCart(cart);
        }),
      );
    },
    updateGiftWrappingMessage: async (message) => {
      if (
        get().giftWrapping.messageBeingUpdated &&
        get().ongoingUpdates.giftWrappingMessage
      ) {
        get().ongoingUpdates.giftWrappingMessage.abort();
        set(
          produce((draft) => {
            draft.ongoingUpdates.giftWrappingMessage = null;
            draft.giftWrapping.messageBeingUpdated = false;
          }),
        );
      }

      const controller = new AbortController();
      const signal = controller.signal;

      set(
        produce((draft) => {
          draft.ongoingUpdates.giftWrappingMessage = controller;
          draft.giftWrapping.messageBeingUpdated = true;
        }),
      );

      const updatedMessageResponse = await fetch(
        `${window.Shopify.routes.root}cart/update.js`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            attributes: {
              'gift-wrapping-message': message,
            },
          }),
          signal,
        },
      );
      const products = await updatedMessageResponse.json();

      set(
        produce((draft) => {
          draft.ongoingUpdates.giftWrappingMessage = null;
          draft.giftWrapping.messageBeingUpdated = false;
          draft.products = deDupeItemsInCart(products);
        }),
      );
    },
    updateNote: async (note) => {
      if (get().noteBeingUpdated && get().ongoingUpdates.note) {
        get().ongoingUpdates.note.abort();
        set(
          produce((draft) => {
            draft.ongoingUpdates.note = null;
            draft.noteBeingUpdated = false;
          }),
        );
      }

      const controller = new AbortController();
      const signal = controller.signal;

      set(
        produce((draft) => {
          draft.ongoingUpdates.note = controller;
          draft.noteBeingUpdated = true;
        }),
      );

      const updatedNoteResponse = await fetch(
        `${window.Shopify.routes.root}cart/update.js`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            note,
          }),
          signal,
        },
      );
      const products = await updatedNoteResponse.json();

      set(
        produce((draft) => {
          draft.ongoingUpdates.note = null;
          draft.noteBeingUpdated = false;
          draft.products = deDupeItemsInCart(products);
        }),
      );
    },
    updateRecommendations: async () => {
      // TODO: Use local storage to cache recommendations
      if (!get().recommendations.enabled) return;

      // Can be either 'proportional' or 'equal'
      const weightDistribution = 'equal';
      const intent = get().recommendations.intent;
      const newRecommendations = {};
      const existingRecommendations = get().recommendations.items;
      const existingRecommendationsIds = Object.keys(existingRecommendations);
      const productIds = get().products.items.reduce((ids, item) => {
        // TODO: Also exclude gift wrapping product id
        if (!item.gift_card && !ids.includes(item.product_id.toString()))
          ids.push(item.product_id.toString());

        return ids;
      }, []);

      const remainingProductIds = existingRecommendationsIds.filter(
        (productId) => productIds.includes(productId),
      );

      const remainingRecommendations = remainingProductIds.reduce(
        (recommendations, productId) => {
          return {
            ...recommendations,
            [productId]: existingRecommendations[productId],
          };
        },
        {},
      );

      const newProductIds = productIds.filter(
        (productId) => !existingRecommendationsIds.includes(productId),
      );

      // Products in the cart are excluded from the recommendations
      // so when the product is removed from the cart, the recommendations
      // should be updated, together with fetching ones for new products
      await Promise.all(
        [
          ...(remainingProductIds.length < existingRecommendationsIds.length
            ? remainingProductIds
            : []),
          ...newProductIds,
        ].map(async (productId) => {
          const data = await fetch(
            `${window.Shopify.routes.root}recommendations/products.json?product_id=${productId}&intent=${intent}`,
          );
          const recommendations = await data.json();

          if (remainingProductIds.includes(productId)) {
            remainingRecommendations[productId] = recommendations;
          } else {
            newRecommendations[productId] = recommendations;
          }
        }),
      );

      const finalRecommendations = {
        ...remainingRecommendations,
        ...newRecommendations,
      };

      // Calculate and set weight
      const calculateWeight = (productId) => {
        const totalPrice = get()
          .products.items.filter(
            (item) => item.product_id.toString() === productId,
          )
          .reduce((total, item) => {
            return total + item.line_price;
          }, 0);

        return totalPrice / get().products.total_price;
      };

      set(
        produce((draft) => {
          draft.recommendations.items = Object.keys(
            finalRecommendations,
          ).reduce((recommendations, productId) => {
            recommendations[productId] = {
              weight:
                weightDistribution === 'proportional'
                  ? calculateWeight(productId)
                  : 1 /
                    get().products.items.reduce((items, item) => {
                      if (!items.includes(item.id)) items.push(item.id);

                      return items;
                    }, []).length,
              // Packages are recommended like anything else. A package cannot
              // be bought on its own — the cart rules would pull it straight
              // back out — so its card carries no add to cart button and sends
              // the shopper to the package page instead; see product-cards.js.
              products: finalRecommendations[productId].products || [],
            };

            return recommendations;
          }, {});
        }),
      );
    },
  }));
};

/**
 * Runs the package check after every cart update, whatever caused it: a line
 * removed, a bike quantity stepped up or down, gift wrapping toggled. All of
 * them go through this store, so subscribing here covers every trigger in the
 * theme at once. The check writes to the store itself, hence the re-entry
 * guard; the write it makes leaves every package matched to its bikes, so the
 * run it triggers finds nothing to do.
 */
const watchPackagePairing = () => {
  let validating = false;

  window.theme.cart.store.subscribe(async (state, prevState) => {
    if (validating || state.products === prevState.products) return;
    // The cart the store holds is what the customer is looking at, so it is
    // enough to decide whether anything needs checking; the check itself reads
    // the cart back from Shopify before it changes a thing.
    if (!packageCorrections(state.products)) return;

    validating = true;
    try {
      await window.theme.cart.store.getState().validateCartPackages();
    } catch (e) {
      console.error(e);
    } finally {
      validating = false;
    }
  });
};

initiateStore();
resetFreeShippingAnimation();
watchPackagePairing();

await window.theme.cart.store.getState().syncGiftWrapping();

// The cart may have arrived at this page already out of step — a bike removed or
// its quantity changed on the cart page, in another tab, or before a bounce
// back. The cart Liquid rendered into the page is the server's own, so it
// settles that without a request of its own.
if (packageCorrections(window.theme.cart.store.getState().products)) {
  try {
    await window.theme.cart.store.getState().validateCartPackages();
  } catch (e) {
    console.error(e);
  }
}
